import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12 } from "styled-components";
import React from "react";
import { takeFirst } from "~/features/app/utils";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { isLinkValid } from "~/features/ui/link/utils";
import { useGalleryBlock } from "~/features/blocks/inventory/gallery/context";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
export type GallerySlideContentPlacement = "inset" | "bottom";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
export type GallerySlideViewProps = {
  contentPlacement?: GallerySlideContentPlacement;
  label?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  renderDesktopMedia: (videoRef: React.RefObject<HTMLVideoElement | null>) => React.ReactNode;
  renderMobileMedia: (videoRef: React.RefObject<HTMLVideoElement | null>) => React.ReactNode;
  supportLink?: PlainLink | Array<PlainLink | null | undefined> | null;
  text?: React.ReactNode;
  title?: string | null;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export function GallerySlideView({
  contentPlacement = "inset",
  label,
  link: linkValue,
  renderDesktopMedia,
  renderMobileMedia,
  supportLink: supportLinkValue,
  text,
  title
}: GallerySlideViewProps) {
  const {
    theme,
    mode,
    layout
  } = useGalleryBlock();
  const desktopVideoRef = React.useRef<HTMLVideoElement>(null);
  const mobileVideoRef = React.useRef<HTMLVideoElement>(null);
  const cta = takeFirst(linkValue);
  const supportCta = takeFirst(supportLinkValue);
  const icon = getPlainLinkIcon(cta);
  const supportIcon = getPlainLinkIcon(supportCta);
  const themeStyle = getThemeStyle({
    mode,
    theme
  });
  const insetContent = <_StyledDiv $_css={[{
    "position": "absolute",
    "inset": "1.6rem",
    "display": "flex",
    "overflow": "hidden",
    "borderRadius": "2px",
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))",
    "&::before": {
      "content": "var(--tw-content)",
      "position": "absolute",
      "inset": "0",
      "--tw-content": "\"\"",
      "background": "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)"
    },
    "@media (min-width: 1024px)": {
      "inset": "0"
    }
  }, layout === "full-bleed" ? {
    "alignItems": "flex-end",
    "@media (min-width: 1024px)": {
      "alignItems": "flex-start",
      "&::before": {
        "content": "var(--tw-content)",
        "position": "absolute",
        "inset": "0",
        "--tw-content": "\"\"",
        "background": "linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)"
      }
    }
  } : {
    "alignItems": "flex-end"
  }]}>
      <_StyledDiv2 $_css2={[{
      "pointerEvents": "auto",
      "position": "relative",
      "zIndex": "10",
      "display": "flex",
      "flexDirection": "column",
      "gap": "2.4rem",
      "padding": "var(--spacing-lg)"
    }, layout === "full-bleed" && {
      "@media (min-width: 1024px)": {
        "flex": "1 1 0%",
        "flexDirection": "row",
        "justifyContent": "space-between"
      }
    }]}>
        <_StyledDiv3>
          <_StyledH $_css3={[{
          "whiteSpace": "nowrap",
          "fontSize": "1.8rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.018rem",
          "fontWeight": "400",
          "@media (min-width: 1024px)": {
            "whiteSpace": "normal"
          }
        }]}>{title}</_StyledH>
          {text && <_StyledDiv4 $_css4={[{
          "fontSize": "1.4rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.014000000000000002rem",
          "fontWeight": "400",
          "@media (min-width: 1024px)": {
            "fontSize": "1.4rem",
            "lineHeight": "1.5",
            "letterSpacing": "0.014000000000000002rem",
            "fontWeight": "400"
          }
        }]}>{text}</_StyledDiv4>}
          {label && <_StyledP>{label}</_StyledP>}
        </_StyledDiv3>
        {isLinkValid(cta) && <_StyledDiv5 $_css5={[{
        "display": "flex",
        "alignItems": "center",
        "gap": "var(--spacing-lg)"
      }]}>
            <Button as={Link} {...cta} icon={icon} variant="secondary" />
            <Button as={Link} {...supportCta} icon={supportIcon} variant="underline" />
          </_StyledDiv5>}
      </_StyledDiv2>
    </_StyledDiv>;
  return <_StyledDiv6>
      <_StyledDiv7>
        <_StyledDiv8 $_css6={[{
        "position": "relative",
        "display": "none",
        "width": "100%",
        "@media (min-width: 1024px)": {
          "display": "block"
        }
      }, contentPlacement === "inset" && {
        "height": "100%"
      }]}>
          {renderDesktopMedia(desktopVideoRef)}

          {contentPlacement === "inset" && insetContent}
        </_StyledDiv8>
        <_StyledDiv9>
          {renderMobileMedia(mobileVideoRef)}
          {contentPlacement === "inset" && insetContent}
        </_StyledDiv9>
      </_StyledDiv7>
      {contentPlacement === "bottom" && <_StyledDiv10 $_css7={[{
      "display": "flex",
      "flexDirection": "column",
      "gap": "1.6rem",
      "borderTopWidth": "1px",
      "paddingLeft": "4rem",
      "paddingRight": "4rem",
      "paddingTop": "2.4rem",
      "paddingBottom": "2.4rem",
      "@media (min-width: 1024px)": {
        "height": "100%",
        "paddingTop": "4rem",
        "paddingBottom": "4rem"
      }
    }, themeStyle.border]}>
          {text}
          {isLinkValid(cta) && <div>
              <Button as={Link} {...cta} icon={icon} variant="underline" />
            </div>}
        </_StyledDiv10>}
    </_StyledDiv6>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1inrh5s-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1inrh5s-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1inrh5s-2"
})({
  "display": "flex",
  "flexDirection": "column",
  "gap": "0.4rem",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledH = _styled4("h3").withConfig({
  componentId: "sc-sf-1inrh5s-3"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-1inrh5s-4"
})`${p => p.$_css4}`;
var _StyledP = _styled6("p").withConfig({
  componentId: "sc-sf-1inrh5s-5"
})({
  "display": "none",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "opacity": "0.5",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-1inrh5s-6"
})`${p => p.$_css5}`;
var _StyledDiv6 = _styled8("div").withConfig({
  componentId: "sc-sf-1inrh5s-7"
})({
  "display": "flex",
  "height": "100%",
  "width": "100%",
  "flexDirection": "column"
});
var _StyledDiv7 = _styled9("div").withConfig({
  componentId: "sc-sf-1inrh5s-8"
})({
  "padding": "1.6rem",
  "@media (min-width: 1024px)": {
    "height": "100%",
    "padding": "4rem"
  }
});
var _StyledDiv8 = _styled10("div").withConfig({
  componentId: "sc-sf-1inrh5s-9"
})`${p => p.$_css6}`;
var _StyledDiv9 = _styled11("div").withConfig({
  componentId: "sc-sf-1inrh5s-10"
})({
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv10 = /*#__PURE__*/_styled12("div").withConfig({
  displayName: "gallery-slide___StyledDiv10",
  componentId: "sc-wlc7qq-0"
})(["", ""], p => p.$_css7);